/**
 * Product Datasheet v2 JavaScript
 * Handles: dropdown interaction on product pages, project info from URL params,
 * timestamp generation, and print/PDF functionality.
 *
 * Product data is rendered server-side via Liquid in product.datasheet-v2.liquid
 */

(function() {
  'use strict';

  window.theme = window.theme || {};

  theme.datasheet = {
    initialized: false,

    init: function() {
      if (this.initialized) return;
      this.initialized = true;

      var datasheetPage = document.querySelector('[data-datasheet-page]');
      if (datasheetPage) {
        this.handleProjectInfo();
        this.generateTimestamp();
        this.setPdfTitle();
        window.addEventListener('beforeprint', function() {
          theme.datasheet.setPdfTitle();
        });
        this.setupManualPrint();
        return;
      }

      // Not on the datasheet page — initialize dropdown on product page
      this.initDropdown();
    },

    /* ========================================================================
       DROPDOWN (Product Page)
       ======================================================================== */

    initDropdown: function() {
      var containers = document.querySelectorAll('[data-datasheet-container]');
      if (!containers.length) return;

      for (var i = 0; i < containers.length; i++) {
        this.initSingleDropdown(containers[i]);
      }

      // Global click listener to close all open dropdowns when clicking outside
      document.addEventListener('click', function(e) {
        var allContainers = document.querySelectorAll('[data-datasheet-container]');
        for (var j = 0; j < allContainers.length; j++) {
          if (!allContainers[j].contains(e.target)) {
            var t = allContainers[j].querySelector('[data-datasheet-trigger]');
            var d = allContainers[j].querySelector('[data-datasheet-dropdown]');
            var cv = d ? d.querySelector('[data-choice-view]') : null;
            var fv = d ? d.querySelector('[data-form-view]') : null;
            if (t && d) theme.datasheet.closeDropdown(t, d, cv, fv);
          }
        }
      });
    },

    initSingleDropdown: function(container) {
      var trigger = container.querySelector('[data-datasheet-trigger]');
      if (!trigger) return;

      var dropdown = container.querySelector('[data-datasheet-dropdown]');

      if (!dropdown) {
        trigger.addEventListener('click', function() {
          theme.datasheet.downloadPdf(container, null, trigger);
        });
        return;
      }

      var choiceView = dropdown.querySelector('[data-choice-view]');
      var formView = dropdown.querySelector('[data-form-view]');
      var showFormBtn = dropdown.querySelector('[data-show-form]');
      var skipBtn = dropdown.querySelector('[data-skip-form]');
      var cancelBtn = dropdown.querySelector('[data-cancel-form]');
      var projectForm = dropdown.querySelector('[data-project-form]');

      trigger.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
          theme.datasheet.closeDropdown(trigger, dropdown, choiceView, formView);
        } else {
          // Close all other open dropdowns first
          theme.datasheet.closeAllDropdowns();
          theme.datasheet.openDropdown(trigger, dropdown);
        }
      });

      if (showFormBtn && formView && choiceView) {
        showFormBtn.addEventListener('click', function() {
          choiceView.setAttribute('data-hidden', 'true');
          formView.setAttribute('data-visible', 'true');
          var firstInput = formView.querySelector('input');
          if (firstInput) setTimeout(function() { firstInput.focus(); }, 100);
          // The form is tall; on small screens its lower fields (Notes) can fall
          // below the fold. Bring the whole panel into view once it has expanded.
          setTimeout(function() {
            dropdown.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 320);
        });
      }

      if (skipBtn) {
        skipBtn.addEventListener('click', function() {
          theme.datasheet.downloadPdf(container, null, skipBtn);
          theme.datasheet.closeDropdown(trigger, dropdown, choiceView, formView);
        });
      }

      if (cancelBtn && formView && choiceView) {
        cancelBtn.addEventListener('click', function() {
          formView.setAttribute('data-visible', 'false');
          choiceView.setAttribute('data-hidden', 'false');
          if (projectForm) projectForm.reset();
        });
      }

      if (projectForm) {
        projectForm.addEventListener('submit', function(e) {
          e.preventDefault();
          var formData = new FormData(projectForm);
          var extra = {};
          for (var pair of formData.entries()) {
            if (pair[1] && pair[1].trim()) extra[pair[0]] = pair[1].trim();
          }
          var submitBtn = projectForm.querySelector('[data-generate-datasheet]');
          theme.datasheet.downloadPdf(container, extra, submitBtn);
          projectForm.reset();
          theme.datasheet.closeDropdown(trigger, dropdown, choiceView, formView);
        });
      }
    },

    closeAllDropdowns: function() {
      var containers = document.querySelectorAll('[data-datasheet-container]');
      for (var i = 0; i < containers.length; i++) {
        var t = containers[i].querySelector('[data-datasheet-trigger]');
        var d = containers[i].querySelector('[data-datasheet-dropdown]');
        var cv = d ? d.querySelector('[data-choice-view]') : null;
        var fv = d ? d.querySelector('[data-form-view]') : null;
        if (t && d) this.closeDropdown(t, d, cv, fv);
      }
    },

    openDropdown: function(trigger, dropdown) {
      trigger.setAttribute('aria-expanded', 'true');
      dropdown.setAttribute('aria-hidden', 'false');
    },

    closeDropdown: function(trigger, dropdown, choiceView, formView) {
      trigger.setAttribute('aria-expanded', 'false');
      dropdown.setAttribute('aria-hidden', 'true');
      setTimeout(function() {
        if (choiceView) choiceView.setAttribute('data-hidden', 'false');
        if (formView) formView.setAttribute('data-visible', 'false');
      }, 300);
    },

    /* ========================================================================
       DOWNLOAD PDF
       Calls the App Proxy -> Cloudflare Worker -> Browser Rendering, then
       downloads the generated PDF. Shows a loading state on the button and
       falls back to opening the on-page datasheet if the service is down, so
       the customer always gets their datasheet.
       ======================================================================== */

    downloadPdf: function(container, extraParams, btn) {
      var handle = container.getAttribute('data-product-handle');
      var fallbackUrl = container.getAttribute('data-datasheet-url');

      // No window.open anywhere: a successful click DOWNLOADS the PDF directly
      // (proper filename, no tab jump, never a pop-up). Only if the worker is
      // unavailable do we fall back to the on-page ?view=datasheet view, via a
      // same-tab navigation (always allowed, can't be pop-up-blocked). With the
      // worker as the normal path, that fallback is now a rare safety net.
      var buildFallbackUrl = function() {
        var fb = fallbackUrl;
        if (extraParams) {
          var pf = new URLSearchParams();
          for (var kf in extraParams) { if (extraParams[kf]) pf.append(kf, extraParams[kf]); }
          if (pf.toString()) fb = fallbackUrl + '&' + pf.toString();
        }
        return fb;
      };
      var goFallback = function() {
        var fb = buildFallbackUrl();
        if (fb) window.location.href = fb;
      };

      // Worker URL: theme setting / js-variables default if present, otherwise a
      // hard-coded last resort so the button still calls the worker even when the
      // live theme's js-variables hasn't been re-pushed (datasheetPdfUrl empty).
      var base = (window.theme && window.theme.datasheetPdfUrl) ? String(window.theme.datasheetPdfUrl).replace(/\/+$/, '') : 'https://datasheet-pdf-worker.marketing-ef0.workers.dev';

      // Show the spinner/check on the visible trigger button (the skip/generate
      // button lives inside the dropdown, which closes immediately, so its state
      // would never be seen).
      var trigger = container.querySelector('[data-datasheet-trigger]');
      var setState = function(state) {
        if (!trigger) return;
        trigger.classList.remove('is-generating', 'is-done');
        if (state) trigger.classList.add(state);
        if (state === 'is-generating') trigger.setAttribute('aria-busy', 'true');
        else trigger.removeAttribute('aria-busy');
      };

      // PDF worker URL not configured yet -> fall back to the on-page datasheet.
      if (!base) { goFallback(); return; }

      var params = new URLSearchParams();
      if (handle) params.set('handle', handle);
      if (extraParams) {
        for (var k in extraParams) {
          if (extraParams[k]) params.set(k, extraParams[k]);
        }
      }
      var url = base + '/datasheet?' + params.toString();

      setState('is-generating');

      var filename = 'datasheet.pdf';

      fetch(url)
        .then(function(res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          var ct = res.headers.get('content-type') || '';
          if (ct.indexOf('application/pdf') === -1) throw new Error('Unexpected content-type: ' + ct);
          var cd = res.headers.get('content-disposition') || '';
          var m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
          if (m && m[1]) { try { filename = decodeURIComponent(m[1]); } catch (e) { filename = m[1]; } }
          return res.blob();
        })
        .then(function(blob) {
          // Direct download with the proper filename — no new tab.
          var objUrl = URL.createObjectURL(blob);
          var a = document.createElement('a');
          a.href = objUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(function() { URL.revokeObjectURL(objUrl); }, 5000);
          setState('is-done');
          setTimeout(function() { setState(null); }, 1600);
        })
        .catch(function(err) {
          if (window.console) console.warn('Datasheet PDF failed, falling back to on-page view:', err);
          goFallback();
          setState(null);
        });
    },

    /* ========================================================================
       PROJECT INFO (from URL params)
       ======================================================================== */

    handleProjectInfo: function() {
      var urlParams = new URLSearchParams(window.location.search);
      var projectBox = document.getElementById('project-info-box');
      if (!projectBox) return;

      var typeElement = document.getElementById('project-type');
      var projectElement = document.getElementById('project-name');
      var architectElement = document.getElementById('project-architect');
      var notesElement = document.getElementById('project-notes');
      var hasAnyInfo = false;

      // Type Number
      var typeValue = urlParams.get('type');
      if (typeValue && typeElement) {
        typeElement.textContent = typeValue;
        hasAnyInfo = true;
      } else {
        var typeRow = document.querySelector('[data-project-field="type"]');
        if (typeRow) typeRow.style.display = 'none';
      }

      // Project Name
      var projectValue = urlParams.get('project');
      if (projectValue && projectElement) {
        projectElement.textContent = projectValue;
        hasAnyInfo = true;
      } else {
        var projectRow = document.querySelector('[data-project-field="project"]');
        if (projectRow) projectRow.style.display = 'none';
      }

      // Architect / Designer
      var architectValue = urlParams.get('architect');
      if (architectValue && architectElement) {
        architectElement.textContent = architectValue;
        hasAnyInfo = true;
      } else {
        var architectRow = document.querySelector('[data-project-field="architect"]');
        if (architectRow) architectRow.style.display = 'none';
      }

      // Notes
      var notesValue = urlParams.get('notes');
      if (notesValue && notesElement) {
        notesElement.textContent = notesValue;
        hasAnyInfo = true;
      } else {
        var notesRow = document.querySelector('[data-project-field="notes"]');
        if (notesRow) notesRow.style.display = 'none';
      }

      if (hasAnyInfo) {
        projectBox.style.display = 'table';
        // Strip the row separator from the LAST VISIBLE row so the card has no
        // dangling line at the bottom. (CSS tr:last-child targets the DOM-last
        // row, which may be one of the rows hidden above.)
        var allRows = projectBox.querySelectorAll('tr');
        var lastVisibleRow = null;
        for (var r = 0; r < allRows.length; r++) {
          if (allRows[r].style.display !== 'none') lastVisibleRow = allRows[r];
        }
        if (lastVisibleRow) {
          var lastCells = lastVisibleRow.querySelectorAll('td');
          for (var cI = 0; cI < lastCells.length; cI++) lastCells[cI].style.borderBottom = '0';
        }
      }
    },

    /* ========================================================================
       TIMESTAMP & PRINT
       ======================================================================== */

    generateTimestamp: function() {
      var el = document.getElementById('generated-timestamp');
      if (!el) return;

      var now = new Date();
      var day = String(now.getDate()).padStart(2, '0');
      var month = String(now.getMonth() + 1).padStart(2, '0');
      var year = now.getFullYear();
      var hours = now.getHours();
      var minutes = String(now.getMinutes()).padStart(2, '0');
      var ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12 || 12;

      el.textContent = 'Generated ' + day + '/' + month + '/' + year + ' @ ' + hours + ':' + minutes + ampm;
    },

    /* ========================================================================
       PDF FILENAME
       Builds "{Project Name} - {Type Number} - {SKU} - {Product Name} -
       Versalux Lighting Systems" from the project-info URL params plus the
       server-exposed SKU / product name, dropping any empty parts. This is the
       suggested filename Chrome uses for the "Save as PDF" destination.
       ======================================================================== */

    buildPdfFilename: function() {
      var params = new URLSearchParams(window.location.search);
      var project = (params.get('project') || '').trim();
      var type = (params.get('type') || '').trim();
      var sku = (window.__datasheetSku || '').trim();
      var name = (window.__datasheetProductName || '').trim();

      var parts = [project, type, sku, name].filter(Boolean); // drop empty parts
      var full = (parts.length ? parts.join(' - ') + ' - ' : '') + 'Versalux Lighting Systems';

      // Sanitize so Windows-Chrome "Save as PDF" never goes blank: dots make
      // Chrome treat the title as having a file extension, and the rest are
      // illegal Windows filename characters.
      return full
        .replace(/\./g, '-')
        .replace(/[<>:"/\\|?*]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    },

    setPdfTitle: function() {
      var title = this.buildPdfFilename();
      if (title) {
        document.title = title;
        window.__datasheetPdfTitle = title;
      }
    },

    setupManualPrint: function() {
      var printBtn = document.getElementById('manual-print-btn');
      if (!printBtn) return;

      printBtn.addEventListener('click', function() {
        // Re-assert the print/PDF filename right before print so Chrome's
        // Save-as-PDF dialog reliably picks it up as the suggested filename.
        theme.datasheet.setPdfTitle();
        document.body.offsetHeight;
        requestAnimationFrame(function() {
          setTimeout(function() {
            window.print();
          }, 300);
        });
      });
    }
  };

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      theme.datasheet.init();
    });
  } else {
    theme.datasheet.init();
  }

  document.addEventListener('page:loaded', function() {
    theme.datasheet.init();
  });

})();
